import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { TruncatedTooltipValue } from '@/components/layout/TruncatedTooltipValue';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { EditIcon } from '@/icons';
import { TrashIcon } from '@/icons/trash-icon';
import { formatDate } from '@/lib/dateFormatter';
import { DeleteEditorsPickPayload } from '@/services/editorsPick/editorsPick.mutation';
import { editorsPick } from '@/services/editorsPick/editorsPick.types';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import UpdateEditorPickFormContainer from '../updateEditorPickFormContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

export type EditorPickStore = editorsPick;

type OpenChargesProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;
type onOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  openConfirmation: OpenChargesProps,
  onOpen: onOpenProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
  mutateAsyncDeleteEditorsPick: (payload: DeleteEditorsPickPayload) => Promise<unknown>,
): ColumnDef<editorsPick>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[3rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const { storeNameJp, storeNameEng, storeLogo } = row.original;
        return (
          <div className="flex items-center space-x-3 max-w-[16rem] truncate">
            <img
              src={storeLogo?.path || '/no-image.jpeg'}
              alt={storeLogo?.alt ?? ''}
              className="w-8 h-8 rounded-full object-cover bg-surface-sunken shrink-0"
            />
            <div className="truncate">
              <Link
                to={
                  row.original.clubCategory === 'premium'
                    ? '/store-information/premium-stores'
                    : '/store-information/basic-stores'
                }
                search={{ q: storeNameEng }}
                className="font-medium text-xs text-link underline"
              >
                {storeNameEng}
              </Link>
              <div className="text-foreground-subtle font-medium text-xs">{storeNameJp}</div>
            </div>
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => {
        const description = row.original.description ?? '';
        return (
          <TruncatedTooltipValue
            content={description}
            className="max-w-[14rem] text-xs"
            tooltipClassName="max-w-[240px] break-words"
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'tags',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.tags')} />,
      cell: ({ row }) => {
        const tags = row.getValue<string[]>('tags');
        const tagsValue = Array.isArray(tags) ? tags.join(', ') : '';

        return (
          <TruncatedTooltipValue
            content={tagsValue}
            className="max-w-[14rem] text-xs text-link"
            tooltipClassName="max-w-[240px] break-words"
          />
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const createdDate = row.original.createdDate;
        return (
          <div className="font-medium whitespace-nowrap text-xs">
            {createdDate ? formatDate(Number(createdDate), 'YYYY/MM/DD') : <div>-</div>}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },

    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        const updatedDate = row.original.updatedDate;
        return (
          <div className="font-medium whitespace-nowrap text-xs">
            {updatedDate ? formatDate(Number(updatedDate), 'YYYY/MM/DD') : <div>-</div>}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('common.page.tableColumn.action')}
          className="whitespace-nowrap"
        />
      ),
      cell: ({ row }) => (
        <div className="flex items-center space-x-4">
          <Can rule={{ allOf: [PERMS.recommendation.editorPick.update] }}>
            <button
              type="button"
              onClick={() => {
                const editorId = row.original.editorPickSno;
                onOpen({
                  modalTitle: 'common.page.modal.manageEditorsPickInfo.title',
                  title: 'Manage Editor’s Pick Info',
                  okText: t('common.page.modal.submit'),
                  size: 'lg',
                  component: <UpdateEditorPickFormContainer key={editorId} editorId={editorId} />,
                });
              }}
              className="cursor-pointer text-foreground-soft hover:text-foreground-label"
            >
              <EditIcon size={16} />
            </button>
          </Can>
          <Can rule={{ allOf: [PERMS.recommendation.editorPick.delete] }}>
            <button
              type="button"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: t('common.page.modal.deleteClubEditors'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  onAction: async () => {
                    await mutateAsyncDeleteEditorsPick({ editorPickId: row.original.editorPickSno });
                  },
                  onCancel: () => {},
                  variant: 'destructive',
                });
              }}
              className="cursor-pointer text-destructive-text hover:text-destructive-text"
            >
              <TrashIcon size={16} />
            </button>
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
